import * as React from 'react'

import { Box, Popover, Text, useToggle } from '@factorialco/deprecated-design-system'
import { EmployeeTimeoffPoliciesFromLeavesQuery } from 'generated/resources/graphql'
import compact from 'lodash/compact'
import includes from 'lodash/includes'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { DateObject } from 'modules/timeoff/lib/createDateObjects'
import getDateRangeFromTree, {
  munchedTreeToDayTree,
} from 'modules/timeoff/lib/getDateRangeFromTree'

import BlockedPeriodInfo from './BlockedPeriodInfo'
import LeaveInfo from './LeaveInfo'
import { styles } from './index.styles'
import './index.styles.css'
import { DayLeaf, MunchedTree } from './store'

interface Props {
  dateTree: MunchedTree
  dateObject: DateObject
  employeeTimeoffPoliciesFromLeavesData: EmployeeTimeoffPoliciesFromLeavesQuery | null
}

const isCurrentMonth = (dateObject: DateObject) => {
  const classes = dateObject.classNames.split(' ')

  return !includes(classes, 'prevMonth') && !includes(classes, 'nextMonth')
}

const getDayLeafForDay = (tree: MunchedTree, date: DateObject): DayLeaf => {
  const key = date.day.format('YYYY-MM-DD')
  if (!tree || !tree[key])
    return {
      blockedPeriods: [],
      companyHolidays: [],
      leaves: [],
    }

  const result = tree[key]
  return result as DayLeaf
}

const Day = ({ dateTree, dateObject, employeeTimeoffPoliciesFromLeavesData }: Props) => {
  const isCurrent = isCurrentMonth(dateObject)
  const classes =
    isCurrent &&
    getDateRangeFromTree(munchedTreeToDayTree(dateTree), dateObject.day).concat(
      dateObject.classNames.split(' ')
    )

  const styleKeys = Object.keys(styles) as (keyof typeof styles)[]
  const isStyle = (className: string): className is keyof typeof styles => {
    return styleKeys.includes(className as keyof typeof styles)
  }

  const classNames = compact(
    ['Months_day'].concat(
      classes
        ? classes.map((className) => {
            if (className === 'start-end') {
              return 'Months_startEnd'
            }

            if (isStyle(className)) {
              return styles[className]
            }

            return ''
          })
        : []
    )
  )

  const { blockedPeriods, companyHolidays, leaves } = getDayLeafForDay(
    dateTree,
    dateObject
  ) as DayLeaf

  const blockedPeriod = blockedPeriods ? blockedPeriods[0] : null
  const leave = leaves ? leaves[0] : null
  const holiday = companyHolidays ? companyHolidays[0] : null
  const backgroundColor = leave && !holiday ? leave.typeColor : 'none'
  const blockedPeriodBackgroundColor = blockedPeriod ? blockedPeriod.periodColor : 'none'
  const hasOnlyBlockedPeriod = blockedPeriod && !leave && !holiday ? true : false
  const blockedPeriodLabel = blockedPeriods
    ? `${i18n.tp('time_off.blocked_period.label', {
        count: blockedPeriods.length,
      })}`
    : ''
  const backgroundColorOpacity =
    (leave || blockedPeriod) && !holiday && dateObject.classNames.includes('weekend') ? '0.5' : '1'
  const title = i18n.t('common.monthly_date', {
    day: dateObject.day.format('Do'),
    month: dateObject.day.format('MMMM'),
  })

  const [open, toggle] = useToggle()

  if ((holiday || leave || blockedPeriod) && isCurrent) {
    return (
      <Popover.ItemList
        attachment="top left"
        targetAttachment="bottom center"
        toggle={toggle}
        open={open}
        title={title}
        gapDivider={false}
      >
        <div
          className={`${classNames.join(' ')} ${'Months_dayWithTooltip'}`}
          style={
            !hasOnlyBlockedPeriod
              ? {
                  backgroundColor,
                  opacity: backgroundColorOpacity,
                  color: 'white',
                  textAlign: 'center',
                  fontWeight: '700',
                }
              : {
                  backgroundColor: blockedPeriodBackgroundColor,
                }
          }
        >
          {dateToString(dateObject.day, 'ui_day_initials_format')}
        </div>
        {holiday && (
          <Box>
            <Box flexDirection="row" justifyContent="spaceBetween" textAlign="left">
              <span className={'Months_calendarHolidayTooltip'}>{holiday.attributes.summary}</span>
            </Box>
          </Box>
        )}
        {leaves &&
          leaves.map((leaveToMap, i) => (
            <LeaveInfo
              key={`leave_${i}`}
              leave={leaveToMap}
              className={'Months_calendarLeaveTooltip'}
              employeeTimeoffPolicyId={
                employeeTimeoffPoliciesFromLeavesData?.timeoff.leaves.find(
                  (leaveData) => leaveData.id === leaveToMap.attributes.id
                )?.employee?.timeoffPolicyId
              }
            />
          ))}
        {blockedPeriods && blockedPeriods.length !== 0 && (
          <Box paddingTop="s12">
            <Box flexDirection="row" justifyContent="spaceBetween" textAlign="left">
              <Text.Normal>
                <span className={'Months_calendarBlockedPeriodTooltip'}>{blockedPeriodLabel}</span>
              </Text.Normal>
            </Box>
            {blockedPeriods &&
              blockedPeriods.map((blockedPeriod, i) => (
                <BlockedPeriodInfo key={`blocked_period_${i}`} blockedPeriod={blockedPeriod} />
              ))}
          </Box>
        )}
      </Popover.ItemList>
    )
  }

  return (
    <div className={classNames.join(' ')}>
      {isCurrent ? <div>{dateToString(dateObject.day, 'ui_day_initials_format')}</div> : ''}
    </div>
  )
}

export default observer(Day)
